import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const RecursiveLanguageModels = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('recursive-language-models-context-rot-ai-efficiency');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    { id: 'context-crisis', title: '1. The Context Crisis' },
    { id: 'problem', title: '2. The Problem: The High Cost of "Forgetful" AI' },
    { id: 'framework', title: '3. Core Framework: How RLMs Actually Work' },
    { id: 'comparison', title: '4. Comparative Analysis: LLMs vs. RLMs' },
    { id: 'use-cases', title: '5. Industry-Specific Value & KPIs' },
    { id: 'outcomes', title: '6. Outcomes: The Technical and Business Edge' },
    { id: 'challenges', title: '7. Challenges & Implementation Realities' },
    { id: 'future', title: '8. Future Outlook (2026 & Beyond)' },
    { id: 'conclusion', title: '9. Conclusion & Call to Action' },
    { id: 'faq', title: '10. Frequently Asked Questions' }
  ];

  const faqData = [
    {
      question: 'What is the main difference between RLMs and traditional LLMs?',
      answer: 'The key difference is in context management. Traditional LLMs process all input context in a single pass, leading to context rot with longer inputs. RLMs actively manage context by delegating it to external environments (like Python REPL) and recursively querying only relevant segments, maintaining performance across vast contexts.'
    },
    {
      question: 'What is "Context Rot" and how do RLMs solve it?',
      answer: 'Context Rot is the degradation of LLM performance when presented with excessively long or complex inputs, leading to information loss, hallucinations, and reduced accuracy. RLMs solve this by programmatically exploring context through code execution, "peeking" and "grepping" through stored variables to access only relevant information dynamically.'
    },
    {
      question: 'Are RLMs slower than traditional LLMs?',
      answer: 'For short, simple tasks, RLMs may introduce overhead due to additional processing steps. However, for long-horizon tasks involving extensive documentation, multi-turn conversations, or large codebases, RLMs are significantly more efficient by focusing compute only on relevant context segments, reducing reprocessing and errors.'
    },
    {
      question: 'What industries benefit most from RLM implementation?',
      answer: 'RLMs excel in industries requiring deep document analysis: Legal & Compliance (contract review, case law analysis), Research & Development (literature review, patent analysis), Customer Support (complex multi-turn conversations), and Software Development (codebase analysis, debugging). Any domain with extensive knowledge bases benefits significantly.'
    },
    {
      question: 'How do RLMs handle security concerns with code execution?',
      answer: 'RLM implementations require robust sandboxing and security protocols since they execute code in external environments. Best practices include isolated execution environments, strict permission controls, input validation, and comprehensive logging for auditability. Enterprise deployments should implement defense-in-depth security strategies.'
    },
    {
      question: 'What ROI can enterprises expect from RLM adoption?',
      answer: 'Documented benefits include 90% reduction in document review time (legal), 50% faster literature reviews (R&D), 30% improvement in first-contact resolution (customer support), and 40% faster code review cycles (software development). Cost savings range from $50k+ per complex legal case to 10-20% reduction in R&D cycle time.'
    },
    {
      question: 'Can RLMs be integrated with existing LLM infrastructure?',
      answer: 'Yes, RLMs are designed as a "thin wrapper" around standard language models. They maintain API compatibility while adding recursive context management capabilities. Integration typically involves setting up the external execution environment (Python REPL) and configuring the recursion logic, without requiring changes to the base LLM.'
    },
    {
      question: 'What is the future roadmap for RLM technology?',
      answer: 'Expected advancements in 2026 include deeper recursion with asynchronous processing to reduce latency, learned context folding through reinforcement learning, standardized RLM frameworks for easier adoption, specialized domain-tailored architectures, and broader integration into agentic AI systems for autonomous task execution.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Comprehensive guide to Recursive Language Models (RLMs) and their role in solving context rot in AI systems.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Category Tag */}
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            {/* Meta Info */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.readTime} read</span>
              </div>
            </div>

            {/* Excerpt */}
            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            {/* Tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
              {/* Section 1 */}
              <h2 id="context-crisis" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                The Context Crisis
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The Artificial Intelligence (AI) landscape is rapidly evolving, with Large Language Models (LLMs) becoming integral to countless applications. A key trend has been the dramatic expansion of context windows, promising LLMs the ability to process vast amounts of information simultaneously. However, as context windows grow, a critical challenge known as <strong style={{ color: '#ffffff' }}>"Context Rot"</strong> emerges. This phenomenon describes the degradation of an LLM's performance when presented with excessively long or complex inputs, leading to a decline in accuracy and understanding. Recursive Language Models (RLMs) represent a pivotal advancement, addressing this limitation by fundamentally changing how language models interact with and manage extensive contextual data, making true AI Efficiency a reality for long-horizon tasks.
              </p>

              {/* Section 2 */}
              <h2 id="problem" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                The Problem: The High Cost of "Forgetful" AI
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Traditional Large Language Models often suffer from a severe operational inefficiency known as "Context Rot." Despite boasting immense context windows (sometimes millions of tokens), their ability to process information uniformly degrades as input length increases. This leads to critical issues such as:
              </p>
              <ul style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Information Loss:', desc: 'Key facts buried deep within long inputs are frequently missed or ignored.' },
                  { title: 'Hallucination & Misinterpretation:', desc: 'Models may fixate on less relevant details, confuse similar concepts, or misinterpret user intent, leading to inaccurate or unreliable outputs.' },
                  { title: 'Increased Reprocessing & Costs:', desc: 'Enterprises relying on LLMs for knowledge work face higher operational costs due to the need for human verification, re-prompting, or breaking down complex tasks into smaller, less efficient chunks.' },
                  { title: 'Reduced Trust & Latency:', desc: 'Trust in AI outputs erodes when results contradict known facts, and latency spikes as models struggle with bloated prompts, impacting user experience and decision-making speed.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                These inefficiencies hinder the deployment of generative AI in real-world scenarios, particularly for complex tasks involving extensive documentation, multi-turn conversations, or large codebases.
              </p>

              {/* Section 3 */}
              <h2 id="framework" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework: How RLMs Actually Work
              </h2>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Definition</h3>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                A Recursive Language Model (RLM) is not a new neural network architecture, but rather a "thin wrapper" around a standard language model. Its core innovation lies in how it manages and accesses context, creating the illusion of a near-infinite context window for the user. From a user's perspective, the interaction remains similar to calling a normal LLM API, but the internal workings are fundamentally different.
              </p>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>How it Works</h3>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                RLMs operate by actively managing their own context through a recursive process. Instead of processing all input at once, an RLM delegates context to an external environment, often a Python REPL (Read-Eval-Print Loop) notebook. Here's a simplified breakdown:
              </p>

              {/* IMAGE 1: Python REPL Context Management */}
              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Code Execution Environment: Python REPL Integration
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1629654297299-c8506221ca97?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  RLMs leverage Python REPL environments to programmatically explore, filter, and partition massive context. The model writes code to "peek" and "grep" through stored variables, enabling dynamic context access without processing everything at once.
                </p>
              </div>

              <ul style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Context Externalization:', desc: 'The potentially massive context is stored as a variable in an external environment (e.g., a Python script).' },
                  { title: 'Root LM Interaction:', desc: 'The initial query is sent to a "root" LLM, which interacts with this environment.' },
                  { title: 'Programmatic Exploration:', desc: 'The root LLM can write and execute code within the REPL. This allows it to programmatically "peek," "grep," filter, or partition the context.' },
                  { title: 'Recursive Sub-Queries:', desc: 'If the root LLM identifies a specific section or problem requiring deeper analysis, it can launch "recursive sub-queries" by calling itself or another smaller LLM within the REPL environment.' },
                  { title: 'Dynamic Context Management:', desc: 'Unlike traditional LLMs that process context passively, RLMs actively manage their context, deciding what information is relevant and when to access it.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Limitations</h3>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Overhead for Simple Tasks:', desc: 'For short and straightforward tasks, the additional steps can make RLMs slower.' },
                  { title: 'Increased Latency:', desc: 'Current implementations often rely on synchronous calls, which can increase latency.' },
                  { title: 'Implementation Complexity:', desc: 'Code execution introduces security and safety challenges.' },
                  { title: 'Fixed System Prompts:', desc: 'System prompts are not always dynamically tailored to different task types.' },
                  { title: 'Recursive Depth:', desc: 'Current experiments primarily explore a recursive depth of one.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Section 4 - Comparison Table */}
              <h2 id="comparison" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis: LLMs vs. RLMs
              </h2>

              {/* IMAGE 2: Context Window Degradation */}
              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Context Rot Visualization: Performance Degradation
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Traditional LLMs suffer accuracy decline with longer inputs (context rot), while RLMs maintain consistent performance by actively managing context. The table below contrasts passive vs. active context handling approaches.
                </p>
              </div>

              <div className="table-wrapper" style={{
                overflowX: 'auto',
                borderRadius: '12px',
                border: '1px solid #262626',
                marginBottom: '2rem'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead style={{ background: '#1A1A1A' }}>
                    <tr>
                      <th style={{
                        textAlign: 'left',
                        padding: '1rem',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderBottom: '1px solid #262626',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                      }}>Feature</th>
                      <th style={{
                        textAlign: 'left',
                        padding: '1rem',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderBottom: '1px solid #262626',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                      }}>Traditional LLMs</th>
                      <th style={{
                        textAlign: 'left',
                        padding: '1rem',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderBottom: '1px solid #262626',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                      }}>Recursive LMs (RLMs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Context Handling', 'Processes entire input context in a single pass.', 'Actively manages and partitions context, recursing on relevant segments.'],
                      ['Context Rot', 'Highly susceptible; performance degrades with longer inputs.', 'Significantly mitigated; maintains performance across vast contexts.'],
                      ['Information Loss', 'Prone to forgetting, hallucinating, or misprioritizing information.', 'Minimizes information loss by dynamic context access.'],
                      ['Active Management', 'Passive; consumes context as given.', 'Active; decides what to "peek" at and when.'],
                      ['Complexity', 'Simpler API calls, less internal control.', 'More complex internal mechanisms.'],
                      ['Efficiency (Long Tasks)', 'Often inefficient due to context rot.', 'More efficient by focusing compute on relevant parts.'],
                      ['Best For', 'Shorter, well-defined tasks.', 'Long-horizon tasks, deep document analysis, agentic workflows.']
                    ].map((row, idx) => (
                      <tr key={idx} style={{ transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(20, 184, 166, 0.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <td style={{
                          padding: '1rem',
                          color: '#14b8a6',
                          fontWeight: '600',
                          borderBottom: idx < 6 ? '1px solid #262626' : 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                        }}>{row[0]}</td>
                        <td style={{
                          padding: '1rem',
                          color: '#e5e7eb',
                          borderBottom: idx < 6 ? '1px solid #262626' : 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                        }}>{row[1]}</td>
                        <td style={{
                          padding: '1rem',
                          color: '#e5e7eb',
                          borderBottom: idx < 6 ? '1px solid #262626' : 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                        }}>{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Section 5 - Use Cases */}
              <h2 id="use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Industry-Specific Value & KPIs
              </h2>

              {/* IMAGE 3: Document Analysis Use Case */}
              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Real-World Application: Deep Document Analysis
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1568992687947-868a62a9f521?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  RLMs excel at analyzing massive document repositories legal contracts, research papers, codebases by recursively diving into relevant sections. Instead of overwhelming the model with everything at once, RLMs intelligently navigate through documents using targeted sub-queries.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {[
                  {
                    title: 'Legal & Compliance',
                    problem: 'Reviewing vast legal code bases, contracts, and case law for specific clauses, precedents, or compliance issues is time-consuming and prone to human error.',
                    kpis: [
                      { metric: '90% reduction', desc: 'in document review time' },
                      { metric: '25% increase', desc: 'in compliance accuracy' },
                      { metric: '$50k+ savings', desc: 'per complex legal case' }
                    ]
                  },
                  {
                    title: 'Research & Development',
                    problem: 'Sifting through extensive research papers, patents, and scientific literature to find specific methodologies, results, or connections across diverse domains.',
                    kpis: [
                      { metric: '50% faster', desc: 'literature review' },
                      { metric: '15% more', desc: 'latent connections discovered' },
                      { metric: '10-20% reduction', desc: 'in R&D cycle time' }
                    ]
                  },
                  {
                    title: 'Customer Support & Knowledge Management',
                    problem: 'AI-powered chatbots often fail in long, multi-turn conversations where crucial information from earlier interactions gets lost.',
                    kpis: [
                      { metric: '30% improvement', desc: 'in first-contact resolution rates' },
                      { metric: '15% decrease', desc: 'in customer churn' },
                      { metric: '20% reduction', desc: 'in agent escalation rates' }
                    ]
                  },
                  {
                    title: 'Software Development & Engineering',
                    problem: 'Analyzing entire code repositories, architectural documentation, and issue logs for debugging, feature development, or security vulnerabilities.',
                    kpis: [
                      { metric: '40% faster', desc: 'code review cycles' },
                      { metric: '20% reduction', desc: 'in critical security vulnerabilities' },
                      { metric: '10% increase', desc: 'in developer productivity' }
                    ]
                  }
                ].map((useCase, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderLeft: '3px solid #14b8a6',
                    borderRadius: '12px',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif'
                    }}>{useCase.title}</h3>
                    <p style={{
                      color: '#e5e7eb',
                      marginBottom: '1rem',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}>
                      <strong style={{ color: '#ffffff' }}>Problem:</strong> {useCase.problem}
                    </p>
                    <p style={{
                      color: '#e5e7eb',
                      fontWeight: '600',
                      marginBottom: '0.5rem',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}>Value (KPIs):</p>
                    <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {useCase.kpis.map((kpi, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#14b8a6',
                            marginTop: '8px',
                            marginRight: '12px',
                            flexShrink: 0
                          }} />
                          <span style={{ color: '#e5e7eb', fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
                            <strong style={{ color: '#14b8a6' }}>{kpi.metric}</strong> {kpi.desc}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Section 6 - Outcomes */}
              <h2 id="outcomes" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Outcomes: The Technical and Business Edge
              </h2>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Technical Benefits</h3>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Near-Infinite Context Window:', desc: 'Overcomes the practical limitations of fixed context windows.' },
                  { title: 'Dynamic Context Management:', desc: 'Intelligently prioritizes and accesses only relevant parts.' },
                  { title: 'Enhanced Tool-Use Capabilities:', desc: 'Robust tool-use through external environment integration.' },
                  { title: 'Improved Accuracy on Long-Range Dependencies:', desc: 'Maintains consistent understanding over very long documents.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Business Benefits</h3>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Significant Cost Savings:', desc: 'Reduces errors, minimizes reprocessing, and automates oversight tasks.' },
                  { title: 'Accelerated Decision-Making:', desc: 'Faster and more accurate analysis enables quicker strategic decisions.' },
                  { title: 'Superior User Experience:', desc: 'Deep context leads to more coherent, helpful AI interactions.' },
                  { title: 'Unlocking New AI Applications:', desc: 'Makes previously impossible applications viable.' },
                  { title: 'Competitive Advantage:', desc: 'Early adoption positions companies at the forefront of AI innovation.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Section 7 - Challenges */}
              <h2 id="challenges" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Implementation Realities
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Implementing Recursive Language Models, while transformative, is not without its complexities. The promise of near-infinite context comes with real engineering challenges. Key considerations include:
              </p>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Increased Complexity in Deployment:', desc: 'Requires careful orchestration of the base LLM with external environments.' },
                  { title: 'Potential for Higher Latency:', desc: 'Sequential recursive calls can introduce latency challenges.' },
                  { title: 'Security and Safety Concerns:', desc: 'Code execution demands robust sandboxing and security protocols.' },
                  { title: 'Resource Intensiveness for Setup:', desc: 'Initial setup and management can be resource-intensive.' },
                  { title: 'Debugging and Observability:', desc: 'Tracing errors with multiple recursive calls is more complex.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Section 8 - Future */}
              <h2 id="future" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook (2026 & Beyond)
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The next 12 months for Recursive Language Models will be marked by rapid advancements focusing on optimization and expanded capabilities. We anticipate:
              </p>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Deeper Recursion and Asynchronous Processing:', desc: 'Greater recursive depths with asynchronous sub-calls to reduce latency.' },
                  { title: 'Learned Context Folding:', desc: 'Advanced RL and fine-tuning for sophisticated context management.' },
                  { title: 'Standardization of RLM Frameworks:', desc: 'More standardized and user-friendly frameworks will emerge.' },
                  { title: 'Specialized RLM Architectures:', desc: 'Domain-tailored RLMs with pre-optimized environments.' },
                  { title: 'Broader Integration into Agentic Systems:', desc: 'RLMs will become foundational for sophisticated AI agents.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Section 9 - Conclusion */}
              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion & Call to Action
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Recursive Language Models represent a significant leap forward in AI's ability to process and understand vast, complex information. By strategically combating the prevalent "Context Rot," RLMs empower AI to maintain coherence, accuracy, and efficiency over data scales previously deemed intractable. This paradigm shift enables deeper insights, automates complex workflows, and fundamentally redefines the potential of language models across diverse industries. While implementation challenges exist, the profound benefits in terms of operational efficiency, cost reduction, and enhanced decision-making underscore the critical role RLMs will play in the next generation of artificial intelligence.
              </p>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions (FAQ)
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Enterprise with RLMs?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Our team of AI experts is available for a personalized consultation to discuss your specific needs. Let's collaborate on a Proof of Concept (POC) to demonstrate the tangible benefits.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Schedule Your Strategic Consultation
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default RecursiveLanguageModels;
